import { useId } from 'react';

import { statusDotClass } from '../lib/appearance';
import {
  hasAnyFilter,
  narrowMinistriesByCoordinations,
  type CalendarFilterOptions,
} from '../lib/filter';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Sheet } from '@/shared/components/ui/sheet';
import { messages } from '@/shared/constants/messages';
import { PROJECT_STATUS_LABELS } from '@/shared/constants/statuses';
import { cn } from '@/shared/lib/utils';
import type { CalendarFilters } from '@/shared/types/domain';

interface CalendarFilterSheetProps {
  open: boolean;
  onClose: () => void;
  filters: CalendarFilters;
  onChange: (next: CalendarFilters) => void;
  options: CalendarFilterOptions;
}

/**
 * **O filtro do calendário virou uma folha de CAIXAS DE SELEÇÃO**
 * (spec-calendar-ux.md §18, decisão 106).
 *
 * ⚠️ Isto emenda a decisão 96, que mantinha quatro `<select>` numa faixa própria
 * da barra. O que quebrou aquele desenho não foi estética: um `<select>` escolhe
 * **um** valor, e o recorte que a agenda pede é quase sempre plural — "Louvor e
 * Diaconia", "Templo e Salão", "o que está aprovado E o que está em análise".
 * Com um valor por dimensão, a pessoa via a igreja inteira ou um ministério só;
 * o meio-termo, que é o caso real, não tinha como ser pedido. Aqui cada
 * coordenação, ministério, espaço e situação é uma caixa, e marcar duas soma as
 * duas.
 *
 * **Centrada, não folha de baixo** (`placement="center"`): a de baixo é para
 * conteúdo que se LÊ antes de decidir — a folha do projeto. Esta é um painel de
 * controle: a pessoa vem, marca, e a grade atrás já mudou. Centrada ela fica
 * equidistante do olho e não cobre a lista do dia.
 *
 * **Sem passo de "aplicar".** Cada clique já recorta a grade (que continua
 * visível ao redor da folha) e já escreve na URL — o mesmo que os seletores
 * faziam. Um "Aplicar" transformaria um ajuste de um clique em três, e criaria
 * um estado intermediário que a URL não descreveria.
 *
 * **Grupo com menos de duas opções não aparece** (decisão 56). Não é escondido
 * por papel — é escondido por *quantas opções aquela pessoa tem*, a mesma
 * doutrina da ADR-012 decisão 4. Marcar a única caixa que existe não recorta
 * nada.
 *
 * **As opções vêm do que está NA TELA**, não da estrutura (`filterOptionsFrom`).
 * Efeito colateral desejado: a folha nunca nomeia algo que a pessoa não pode
 * ver, sem precisar de uma segunda regra de autorização.
 */
export function CalendarFilterSheet({
  open,
  onClose,
  filters,
  onChange,
  options,
}: CalendarFilterSheetProps) {
  const ministries = narrowMinistriesByCoordinations(
    options.ministries,
    filters.coordinationIds,
  );

  const groups = [
    options.coordinations.length > 1 ? (
      <CheckGroup
        key="coordenacao"
        label="Coordenação"
        options={options.coordinations.map((coordination) => ({
          value: coordination.id,
          label: coordination.name,
        }))}
        selected={filters.coordinationIds}
        onToggle={(id) => {
          const coordinationIds = toggle(filters.coordinationIds, id);
          // Desmarcar uma coordenação leva junto os ministérios que só estavam
          // ali por causa dela: senão sobra um recorte invisível, marcado num
          // grupo que a folha deixou de mostrar.
          const visible = new Set(
            narrowMinistriesByCoordinations(options.ministries, coordinationIds).map((m) => m.id),
          );
          onChange({
            ...filters,
            coordinationIds,
            ministryIds: filters.ministryIds?.filter((value) => visible.has(value)),
          });
        }}
      />
    ) : null,

    ministries.length > 1 ? (
      <CheckGroup
        key="ministerio"
        label="Ministério"
        options={ministries.map((ministry) => ({ value: ministry.id, label: ministry.name }))}
        selected={filters.ministryIds}
        onToggle={(id) => onChange({ ...filters, ministryIds: toggle(filters.ministryIds, id) })}
      />
    ) : null,

    options.spaceNames.length > 1 ? (
      <CheckGroup
        key="espaco"
        label="Espaço"
        options={options.spaceNames.map((name) => ({ value: name, label: name }))}
        selected={filters.spaceNames}
        onToggle={(name) =>
          onChange({ ...filters, spaceNames: toggle(filters.spaceNames, name) })
        }
      />
    ) : null,

    options.statuses.length > 1 ? (
      <CheckGroup
        key="situacao"
        label="Situação"
        options={options.statuses.map((status) => ({
          value: status,
          label: PROJECT_STATUS_LABELS[status],
          // É aqui que a cor da grade e a palavra se encontram (§4.3).
          dot: statusDotClass(status),
        }))}
        selected={filters.statuses}
        onToggle={(status) => onChange({ ...filters, statuses: toggle(filters.statuses, status) })}
      />
    ) : null,
  ].filter(Boolean);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={messages.calendar.filterSheetTitle}
      description={messages.calendar.filterSheetDescription}
      placement="center"
      // A folha central nasceu para uma pergunta de uma linha (`max-w-md`);
      // quatro grupos de caixas precisam de duas colunas para não virar uma
      // coluna de rolagem de trinta itens.
      className="max-w-xl"
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => onChange({})}
            disabled={!hasAnyFilter(filters)}
          >
            {messages.calendar.filterSheetClear}
          </Button>
          <Button onClick={onClose}>{messages.calendar.filterSheetDone}</Button>
        </div>
      }
    >
      {groups.length > 0 ? (
        <div className="space-y-5">{groups}</div>
      ) : (
        <p className="text-sm text-muted-foreground">{messages.calendar.filterSheetEmpty}</p>
      )}
    </Sheet>
  );
}

interface CheckOption<T extends string> {
  value: T;
  label: string;
  /** A bolinha da matiz — só a "Situação" tem uma. */
  dot?: string;
}

/**
 * Um grupo de caixas, com o nome da dimensão como legenda.
 *
 * `fieldset`/`legend` e não `div`/`h3`: para quem usa leitor de tela é a legenda
 * que diz de que lista aquela caixa é — sem ela, "Templo" e "Louvor" chegam como
 * dezoito caixas soltas em sequência.
 */
function CheckGroup<T extends string>({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly CheckOption<T>[];
  selected: readonly T[] | undefined;
  onToggle: (value: T) => void;
}) {
  const chosen = new Set(selected ?? []);
  const groupId = useId();

  return (
    <fieldset className="min-w-0">
      <legend className="mb-2 text-sm font-medium">{label}</legend>
      {/* Duas colunas a partir de 640px: listas de ministério e de espaço
          passam de dez itens, e uma coluna só empurraria "Situação" para fora
          da folha em qualquer tela. */}
      <div className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
        {options.map((option) => {
          const id = `${groupId}-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={id}
              className={cn(
                'flex min-w-0 cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm',
                'transition-colors duration-fast ease-out hover:bg-accent',
              )}
            >
              <Checkbox
                id={id}
                checked={chosen.has(option.value)}
                onChange={() => onToggle(option.value)}
              />
              {option.dot ? (
                <span
                  aria-hidden="true"
                  className={cn('size-2 shrink-0 rounded-full', option.dot)}
                />
              ) : null}
              <span className="min-w-0 truncate">{option.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Marcar o que não estava marcado, desmarcar o que estava — e nunca guardar lista vazia. */
function toggle<T extends string>(
  current: readonly T[] | undefined,
  value: T,
): readonly T[] | undefined {
  const list = current ?? [];
  const next = list.includes(value)
    ? list.filter((entry) => entry !== value)
    : [...list, value];
  return next.length > 0 ? next : undefined;
}
